import React, {useCallback, useEffect, useRef, useState} from "react";
import {loadPlayer} from "rtsp-relay/browser";
import config from "../configs/config";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import {Button, Typography} from "@mui/material";
import Webcam from "react-webcam";
import DeleteIcon from "@material-ui/icons/Delete";
import api from "../services/api";
import VideocamIcon from "@material-ui/icons/Videocam";
import UploadIcon from "@mui/icons-material/Upload";
import {grey, indigo} from "@material-ui/core/colors";
import {makeStyles} from '@material-ui/core/styles';
import IPWebcamPicker from "./IPWebcamPicker";
import {Grid} from "@material-ui/core";
import FullScreenDialog from "./FullScreenDialog";

export default function CameraForm({values, setFieldValue}) {
    const classes = useStyles();

    const [openRTSP, setOpenRTSP] = useState(false);
    const [rtspRunning, setRTSPRunning] = useState(false);
    const [loadingRSTP, setLoadingRSTP] = useState(false);

    const [cameraOpened, setCameraOpened] = useState(false);

    const rtspCanvas = useRef(null);

    const webcamRef = useRef(null);

    const [selectedWebcam, setSelectedWebcam] = useState(null);
    const [webcamPickerOpen, setWebcamPickerOpen] = useState(false);

    const [loadedImage, setLoadedImage] = useState(null);

    const connectionEstabilished = () => {
        setLoadingRSTP(true);
    };

    const playStarted = () => {
        setRTSPRunning(true);
        setLoadingRSTP(false);
    }

    const handleCloseRSTP = () => {
        setOpenRTSP(false);
        setLoadingRSTP(false);
        setRTSPRunning(false);
        setWebcamPickerOpen(false);
    }

    useEffect(() => {
        if (openRTSP && selectedWebcam) {
            const {host, username, password} = selectedWebcam;

            setTimeout(() => {
                loadPlayer({
                    url: `${config.relayUrl}/api/stream/?nvr=${host}&cam=1&username=${username}&password=${password}&subtype=0`,
                    canvas: rtspCanvas.current,
                    onSourceEstablished: connectionEstabilished,
                    onPlay: playStarted,
                    onDisconnect: () => setRTSPRunning(false),
                });
            }, 2000);
        }
    }, [openRTSP, selectedWebcam]);

    useEffect(() => {
        if (selectedWebcam) {
            setOpenRTSP(true);
        } else {
            handleCloseRSTP();
        }
    }, [selectedWebcam]);

    const ImagePreview = ({image}) => {
        if (loadedImage === null) {
            const reader = new FileReader();
            reader.onload = () => {
                if (reader.readyState === 2) {
                    setLoadedImage(reader.result)
                }
            }
            reader.readAsDataURL(image);
            // console.log('image converted')
        }
        // console.log('rendering preview');

        return (
            <img
                src={loadedImage}
                alt="Imagem de identificação"
                style={{maxWidth: '100%', maxHeight: '100%'}}
            />
        );
    };

    const captureImage = useCallback(async setFieldValue => {
        const dataUrl = webcamRef.current.getScreenshot();

        if (dataUrl) {
            const mimeType = dataUrl.match(/[^:]\w+\/[\w-+\d.]+(?=;|,)/);

            const image = await fetch(dataUrl)
                .then(res => res.arrayBuffer())
                .then(buffer => new File([buffer], 'face_image.jpeg', {type: mimeType[0]}));

            setFieldValue('face_image', image);
        }
    }, [webcamRef]);

    const takeWebcamSnapshot = async setFieldValue => {
        if (!selectedWebcam) return;

        try {
            const result = await api.get(`/device/take_snapshot/${selectedWebcam.id}`, {
                responseType: 'blob',
            });

            const dataUrl = URL.createObjectURL(result.data);

            const image = await fetch(dataUrl)
                .then(res => res.arrayBuffer())
                .then(buffer => new File([buffer], 'face_image.jpeg', {type: 'image/jpeg'}));

            setFieldValue('face_image', image);
            setSelectedWebcam(null);
            handleCloseRSTP();
        } catch (err) {
            console.log(err);
        }
    };

    const uploadImage = (e, setFieldValue) => {
        const reader = new FileReader()
        reader.onload = () => {
            if (reader.readyState === 2) {
                setFieldValue('face_image', e.target.files[0]);
                // console.log('uploaded face image')
            }
        }
        reader.readAsDataURL(e.target.files[0]);
    }

    return (
        <div className={classes.faceImageContainer}
        >
            <div
                className={classes.faceImageContent}
                style={{
                    width: '100%',
                }}
            >
                {!!openRTSP ? (
                    <div style={{
                        backgroundmaxHeight: '100%',
                        height: '100%',
                        maxWidth: '100%',
                        margin: 'auto',
                        display: 'flex',
                        justifyContent: 'center',
                    }}>
                        <canvas ref={rtspCanvas} style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '8px',
                            objectFit: 'contain'
                        }}></canvas>
                    </div>
                ) : !cameraOpened ? (
                    values.face_image ? (
                        <ImagePreview image={values.face_image}/>
                    ) : (
                        <div className={classes.noImage}>
                            <PhotoCameraIcon/>
                            <Typography variant="subtitle2">
                                SEM IMAGEM
                            </Typography>
                        </div>
                    )
                ) : (
                    <Webcam
                        ref={webcamRef}
                        audio={false}
                        screenshotFormat="image/jpeg"
                        screenshotQuality={1}
                        forceScreenshotSourceSize={true}
                        // width="100%"
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                        }}
                    />
                )}
            </div>
            {values.face_image && (
                <Button
                    className={classes.removeImageButton}
                    size="small"
                    type="button"
                    variant="outlined"
                    color="secondary"
                    endIcon={<DeleteIcon/>}
                    fullWidth
                    onClick={() => {
                        setFieldValue('face_image', null);
                        setLoadedImage(null);
                    }}
                >
                    Remover
                </Button>
            )}
            {
                !values.face_image && (
                    <>
                        <Button
                            className={cameraOpened ? classes.faceImageButton : classes.faceImageButtonDefault}
                            type="button"
                            variant="outlined"
                            color="primary"
                            endIcon={<PhotoCameraIcon/>}
                            fullWidth
                            onClick={async () => {
                                if (cameraOpened) await captureImage(setFieldValue);

                                setCameraOpened(!cameraOpened);
                            }}
                        >
                            {cameraOpened ? 'Tirar foto' : 'Abrir câmera'}
                        </Button>
                        {
                            !cameraOpened && (
                                <>
                                    <Button
                                        className={classes.faceImageButtonDefault}
                                        variant="outlined"
                                        type="button"
                                        color="primary"
                                        fullWidth={true}
                                        onClick={() => setWebcamPickerOpen(true)}
                                        endIcon={<VideocamIcon/>}
                                    >
                                        Selecionar Webcam
                                    </Button>
                                    <Button
                                        className={classes.faceImageButton}
                                        type="button"
                                        variant="outlined"
                                        color="primary"
                                        endIcon={<UploadIcon/>}
                                        fullWidth
                                        component="label"
                                    >
                                        Adicionar Foto
                                        <input
                                            name='map_image'
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => uploadImage(e, setFieldValue)}
                                            hidden
                                            required={false}
                                        />
                                    </Button>
                                </>
                            )
                        }
                    </>
                )
            }
            <IPWebcamPicker
                open={webcamPickerOpen}
                onSelect={webcam => setSelectedWebcam(webcam)}
                onClose={() => setWebcamPickerOpen(false)}
            />
            <FullScreenDialog
                open={openRTSP}
                title={`${selectedWebcam?.name} - ${loadingRSTP ? 'CARREGANDO' : (rtspRunning ? 'CONECTADO' : 'DESCONECTADO')}`}
                onClose={handleCloseRSTP}
            >
                <Grid
                    xs={12}
                    style={{display: 'flex', marginBottom: 20}}
                    justify="center"
                    alignItems="center"
                >
                    <canvas ref={rtspCanvas}
                            style={{height: '100%', width: '60%', borderRadius: '8px', objectFit: 'contain'}}></canvas>
                </Grid>
                {(!loadingRSTP && rtspRunning) && (
                    <Grid item xs={12} style={{textAlign: 'center'}}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={async () => takeWebcamSnapshot(setFieldValue)}
                            endIcon={<PhotoCameraIcon/>}
                        >
                            Tirar foto
                        </Button>
                    </Grid>
                )}
            </FullScreenDialog>
        </div>
    )

}

const useStyles = makeStyles((theme) => ({
    faceImageContainer: {
        width: '100%',
        minHeight: 400,
        border: `solid 1px ${indigo[200]}`,
        borderRadius: 5,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        padding: theme.spacing(1),
    },
    faceImageContent: {
        width: '100%',
        flex: 7,
        backgroundColor: grey[50],
        borderTopLeftRadius: 5,
        borderTopRightRadius: 5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(.5),
    },
    faceImageButton: {
        width: '100%',
        marginTop: theme.spacing(1),
    },

    faceImageButtonDefault: {
        width: '100%',
        marginTop: theme.spacing(1),
    },

    removeImageButton: {
        width: '100%',
        marginTop: theme.spacing(1),
    },
    noImage: {
        textAlign: 'center',
        color: grey[400],
    },
    cameraContainer: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        '& h6 > span': {
            marginLeft: theme.spacing(.5),
        },
        '&:hover': {
            boxShadow: '0 2px 10px 1px rgba(150,150,150,.3)',
        },
    },
}))
